import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { loadSupplierCustomization } from "@/utils/supplierCustomizationCache";

const DEFAULT_SUPPORT_NUMBER = "5511914935333";
const MARKETING_MESSAGE = "Estava olhando o site de vocês e quero saber mais";

const FloatingWhatsApp = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [whatsappNumber, setWhatsAppNumber] = useState<string | null>(null);
  const [eventCode, setEventCode] = useState<string | null>(null);
  const [cartCount, setCartCount] = useState(0);
  const [isQuoteMode, setIsQuoteMode] = useState(false);

  // Consumer-facing pages (supplier scoped)
  const consumerPaths = [
    '/loja',
    '/cardapio',
    '/infos-festa',
    '/checkout',
    '/confirmacao',
    '/assinatura'
  ];

  // App/private paths where the floating button should NOT appear
  const privatePathPrefixes = [
    '/admin',
    '/dashboard',
    '/supplier',
    '/fornecedor',
    '/painel',
    '/login',
    '/cadastro',
    '/esqueci',
    '/atualizar-senha',
    '/pagamento',
    '/portal-cliente',
    '/contrato',
    '/assinar',
    '/onboarding',
    '/resultados-busca',
    '/simulador',
    '/e/', // Passaporte do Evento: página do cliente com barra de abas própria
  ];

  const pathname = location.pathname;
  const isConsumerPage = consumerPaths.some(p => pathname.startsWith(p));
  const isPrivatePage = privatePathPrefixes.some(p => pathname.startsWith(p));
  // Marketing/public page = not consumer scoped, not private, and user not logged in
  const isMarketingPage = !isConsumerPage && !isPrivatePage && !user;

  useEffect(() => {
    const updateInfo = () => {
      const customization = loadSupplierCustomization();
      const storedEventCode = sessionStorage.getItem('ameup_event_code');
      const storedQuoteMode = sessionStorage.getItem('ameup_quote_mode') === 'true' || new URLSearchParams(window.location.search).get('mode') === 'orcamento';
      const cartItemsRaw = sessionStorage.getItem('supplier_cart_items');
      let count = 0;
      try {
        if (cartItemsRaw) {
          const items = JSON.parse(cartItemsRaw);
          count = Array.isArray(items) ? items.length : 0;
        }
      } catch (e) {
        console.error("Error parsing cart items in FloatingWhatsApp:", e);
      }

      setWhatsAppNumber(customization?.whatsappNumber || null);
      setEventCode(storedEventCode);
      setCartCount(count);
      setIsQuoteMode(storedQuoteMode);
    };

    updateInfo();
    window.addEventListener('event-code-updated', updateInfo);
    window.addEventListener('cart-updated', updateInfo);
    window.addEventListener('cart-filters-updated', updateInfo);
    const interval = setInterval(updateInfo, 2000);

    return () => {
      window.removeEventListener('event-code-updated', updateInfo);
      window.removeEventListener('cart-updated', updateInfo);
      window.removeEventListener('cart-filters-updated', updateInfo);
      clearInterval(interval);
    };
  }, []);

  const isSupplierScopedPage = ['/loja', '/cardapio'].some(p => pathname.startsWith(p));

  // Consumer flow visibility rules
  if (!isConsumerPage && !isMarketingPage) return null;
  if (isConsumerPage && isQuoteMode) return null;
  if (isSupplierScopedPage && !whatsappNumber) return null;

  const handleWhatsAppClick = () => {
    let finalNumber: string;
    let message: string;

    if (isMarketingPage) {
      finalNumber = DEFAULT_SUPPORT_NUMBER;
      message = MARKETING_MESSAGE;
    } else {
      const number = whatsappNumber || DEFAULT_SUPPORT_NUMBER;
      const cleanNumber = number.replace(/\D/g, "");
      finalNumber = cleanNumber.startsWith("55") ? cleanNumber : `55${cleanNumber}`;
      message = eventCode
        ? `Olá, estou simulando o evento ${eventCode} e tenho uma dúvida.`
        : "Olá, tenho uma dúvida.";
    }

    const url = `https://wa.me/${finalNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  return (
    <button
      onClick={handleWhatsAppClick}
      className={`fixed right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full shadow-xl transition-all hover:scale-110 active:scale-95 bg-[#25D366] text-white ${cartCount > 0 ? 'bottom-24' : 'bottom-6'}`}
      aria-label="Falar pelo WhatsApp"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="h-6 w-6"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      </svg>
    </button>
  );
};

export default FloatingWhatsApp;
